import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import LanguageMenu from './LanguageMenu';
import { ALLOWED_ROLES } from '../api';
import { buildBackNavigationState } from '../utils/detailNavState';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

/**
 * Верхняя полоса: сворачивание панели, поиск по разделам админки,
 * быстрые ссылки и переключатель темы.
 */
const Topbar = ({ onOpenSidebar, onToggleCollapsed, user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const userDetailsMatch = useMatch({ path: '/users/:userId', end: true });
  const orderDetailsMatch = useMatch({ path: '/orders/:orderId', end: true });
  const complaintDetailsMatch = useMatch({ path: '/complaints/:complaintId', end: true });
  const promoDetailsMatch = useMatch({ path: '/admin/promo-codes/:promoId', end: true });
  const showBack = Boolean(
    userDetailsMatch || orderDetailsMatch || complaintDetailsMatch || promoDetailsMatch
  );

  const role = user?.role;
  const isAdmin = role === ALLOWED_ROLES.ADMIN || role === ALLOWED_ROLES.DEV;
  const isDev = role === ALLOWED_ROLES.DEV;

  // Что ищем: страницы и разделы админки, доступные текущей роли.
  const destinations = useMemo(() => {
    const items = [
      { path: '/home', label: t('nav.home'), icon: 'home', group: t('nav.sections.workspace', { defaultValue: 'Разделы' }) },
      { path: '/orders', label: t('nav.orders'), icon: 'orders', group: t('nav.sections.workspace', { defaultValue: 'Разделы' }) },
      { path: '/users', label: t('nav.users'), icon: 'users', group: t('nav.sections.workspace', { defaultValue: 'Разделы' }) },
      { path: '/complaints', label: t('nav.complaints'), icon: 'complaints', group: t('nav.sections.workspace', { defaultValue: 'Разделы' }) },
      { path: '/logs', label: t('nav.logs', { defaultValue: 'Логи' }), icon: 'database', group: t('nav.sections.workspace', { defaultValue: 'Разделы' }) },
      { path: '/chat', label: t('nav.chat', { defaultValue: 'Чат' }), icon: 'message-square', group: t('nav.sections.tools', { defaultValue: 'Инструменты' }) },
      { path: '/calendar', label: t('nav.calendar', { defaultValue: 'Календарь' }), icon: 'calendar', group: t('nav.sections.tools', { defaultValue: 'Инструменты' }) },
    ];
    if (isAdmin) {
      items.push(
        { path: '/statistics', label: t('nav.statistics', { defaultValue: 'Статистика' }), icon: 'bar-chart-2', group: t('nav.sections.tools', { defaultValue: 'Инструменты' }) },
        { path: '/admin?tab=stats', label: t('admin.dashboard.stats'), icon: 'bar-chart-2', group: t('nav.admin') },
        { path: '/admin?tab=push', label: t('admin.dashboard.notifications'), icon: 'bell', group: t('nav.admin') },
        { path: '/admin?tab=create', label: t('admin.dashboard.createEmployee'), icon: 'user-plus', group: t('nav.admin') },
        { path: '/admin?tab=promo', label: t('admin.dashboard.promoCodes'), icon: 'tag', group: t('nav.admin') },
        { path: '/admin?tab=marketplace', label: t('admin.dashboard.marketplace'), icon: 'shopping-cart', group: t('nav.admin') },
      );
    }
    if (isDev) {
      items.push(
        { path: '/admin?tab=banners', label: t('admin.dashboard.banners'), icon: 'image', group: t('nav.admin') },
        { path: '/admin?tab=public-files', label: t('admin.dashboard.publicFiles'), icon: 'upload', group: t('nav.admin') },
      );
    }
    return items;
  }, [t, isAdmin, isDev]);

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return destinations;
    return destinations.filter((item) => item.label.toLowerCase().includes(term));
  }, [query, destinations]);

  // Группируем по разделам — как в палитре команд.
  const groupedResults = useMemo(() => {
    const groups = [];
    results.forEach((item) => {
      const last = groups[groups.length - 1];
      if (last && last.group === item.group) last.items.push(item);
      else groups.push({ group: item.group, items: [item] });
    });
    return groups;
  }, [results]);

  const themeLabel = theme === 'dark'
    ? t('nav.theme.switchToLight', { defaultValue: 'Светлая тема' })
    : t('nav.theme.switchToDark', { defaultValue: 'Тёмная тема' });

  // Быстрая ссылка справа — возврат на главную.
  const quickLinks = [{ path: '/home', label: t('nav.home') }];

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    setSearchOpen(false);
    setQuery('');
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) setHighlighted(0);
  }, [searchOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      // e.code не зависит от раскладки: в русской Ctrl+K даёт e.key === 'л'.
      const isK = e.code === 'KeyK' || e.key?.toLowerCase() === 'k';
      if (!isK || !(e.metaKey || e.ctrlKey) || e.altKey) return;

      // Перехватываем до браузера (в Chrome Ctrl+K уводит фокус в адресную строку).
      e.preventDefault();
      e.stopPropagation();

      setSearchOpen((wasOpen) => {
        if (wasOpen) {
          inputRef.current?.focus();
          inputRef.current?.select();
          return true;
        }
        return true;
      });
    };
    // capture: обработчик срабатывает раньше остальных слушателей страницы
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  const openResult = (item) => {
    navigate(item.path);
    setSearchOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[highlighted]) {
      openResult(results[highlighted]);
    } else if (e.key === 'Escape') {
      setSearchOpen(false);
    }
  };

  // Возврат по стеку деталей — та же логика, что была в прежнем навбаре.
  const handleBack = () => {
    const s = location.state;
    const stack = s?.navBackStack;
    if (Array.isArray(stack) && stack.length > 0) {
      const target = stack[stack.length - 1];
      navigate(target, { state: buildBackNavigationState(s, target, stack.slice(0, -1)) });
      return;
    }
    if (userDetailsMatch) {
      if (s?.fromLogs) {
        const target = typeof s.fromLogs === 'string' ? s.fromLogs : s.fromLogs.path;
        navigate(target, typeof s.fromLogs === 'string' ? undefined : { state: { fromLogs: s.fromLogs } });
        return;
      }
      if (s?.fromUser) return navigate(s.fromUser);
      if (s?.fromOrder) return navigate(s.fromOrder);
      if (s?.fromComplaint) return navigate(s.fromComplaint);
      if (s?.fromPromo) return navigate(s.fromPromo);
      if (s?.fromUsers) return navigate('/users', { state: { fromUsers: s.fromUsers } });
      return navigate('/users');
    }
    if (orderDetailsMatch) {
      if (s?.fromUser) return navigate(s.fromUser);
      if (s?.fromComplaint) return navigate(s.fromComplaint);
      const listParams = s?.fromOrders?.searchParams;
      return listParams ? navigate('/orders', { state: { searchParams: listParams } }) : navigate('/orders');
    }
    if (complaintDetailsMatch) {
      if (s?.fromUser) return navigate(s.fromUser);
      return navigate('/complaints');
    }
    if (promoDetailsMatch) return navigate('/admin?tab=promo');
    return navigate(-1);
  };

  return (
    <header className="tb">
      <button
        type="button"
        className="tb-btn tb-burger"
        onClick={onOpenSidebar}
        aria-label={t('nav.openMenu', { defaultValue: 'Открыть меню' })}
      >
        <Icon name="menu" size={20} />
      </button>

      <button
        type="button"
        className="tb-btn tb-collapse"
        onClick={onToggleCollapsed}
        title={t('nav.collapse', { defaultValue: 'Свернуть меню' })}
      >
        <Icon name="panel-left" size={18} />
      </button>

      {showBack && (
        <button
          type="button"
          className="tb-btn"
          onClick={handleBack}
          aria-label={t('common.back', { defaultValue: 'Назад' })}
          title={t('common.back', { defaultValue: 'Назад' })}
        >
          <Icon name="chevron-left" size={20} />
        </button>
      )}

      <button type="button" className="tb-search" onClick={() => setSearchOpen(true)}>
        <Icon name="search" size={16} />
        <span className="tb-search-text">
          {t('nav.searchPlaceholder', { defaultValue: 'Поиск по разделам...' })}
        </span>
        <kbd className="tb-kbd">{isMac ? '⌘' : 'Ctrl'} K</kbd>
      </button>

      {searchOpen && createPortal(
        <div className="tb-palette-backdrop" onMouseDown={() => setSearchOpen(false)}>
          <div
            className="tb-palette"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            ref={searchRef}
          >
            <header className="tb-palette-head">
              <h3>{t('nav.searchTitle', { defaultValue: 'Что вам нужно?' })}</h3>
              <button type="button" className="tb-btn" onClick={() => setSearchOpen(false)}>
                <Icon name="x" size={18} />
              </button>
            </header>

            <div className="tb-palette-input">
              <Icon name="search" size={16} />
              <input
                ref={inputRef}
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={t('nav.searchPlaceholder', { defaultValue: 'Поиск по разделам...' })}
              />
            </div>

            <div className="tb-palette-list">
              {results.length === 0 && (
                <div className="tb-search-empty">
                  {t('common.notFound', { defaultValue: 'Ничего не найдено' })}
                </div>
              )}

              {groupedResults.map((group) => (
                <div className="tb-palette-group" key={group.group}>
                  <span className="tb-palette-group-label">{group.group}</span>
                  {group.items.map((item) => {
                    const index = results.indexOf(item);
                    return (
                      <button
                        key={item.path}
                        type="button"
                        className={`tb-palette-item${index === highlighted ? ' active' : ''}`}
                        onMouseEnter={() => setHighlighted(index)}
                        onClick={() => openResult(item)}
                      >
                        <Icon name={item.icon} size={17} />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="tb-spacer" />

      <div className="tb-right">
        <LanguageMenu />

        <div className="tb-quick">
          {quickLinks.map((link) => (
            <button
              key={link.path}
              type="button"
              className={`tb-quick-link${location.pathname === link.path ? ' active' : ''}`}
              onClick={() => navigate(link.path)}
            >
              {link.label}
            </button>
          ))}
        </div>

        <span className="tb-divider" aria-hidden="true" />

        <span className="ui-tip-wrap">
          <button
            type="button"
            className="tb-btn tb-theme"
            onClick={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
            aria-label={themeLabel}
          >
            <Icon name={theme === 'dark' ? 'theme-light' : 'theme-dark'} size={20} />
          </button>
          <span className="ui-tip ui-tip--end" aria-hidden="true">{themeLabel}</span>
        </span>
      </div>
    </header>
  );
};

export default Topbar;
